import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { storeDispatch, getStoreState } from '../state/Store';
import * as Actions from '../state/actions';

import Layer from '../data/Layer';
import { MotionSource } from '../motion/MotionSource';
import LayersList from './LayersList';
import { SortableContainer } from 'react-sortable-hoc';
import SortEvent from './SortEvent';
import { MAX_FRAMES_AMOUNT } from '../data/Config';
import {
  setCSSVar,
  getCSSVarValue,
  generateClassName,
  hasParentWithClassName,
  isPickingColor,
  isMobileLayout
} from '../utils';
import { ConfirmationBar } from './components/ConfirmationBar';
import { NewFrameTypeMenu } from './NewFrameTypeMenu';
import { MotionSourceView } from './MotionSourceView';
import { Soundtrack } from '../audio/Soundtrack';
import { MotionSourceEditView } from './MotionSourceEditView';
import { MuteMotionSource } from '../motion/MuteMotionSource';
import { TemplateEditMode } from '../state/AppState';
import { TabBarView } from '../components/TabBarView';
import { TemplateUIView } from './TemplateUIView';
import { Effect, EffectData } from '@beatflyer/shaders-lib';
import { AddEffectCommand } from 'state/commands/AddEffectCommand';
import { EditTimelineCommand } from 'state/commands/EditTimelineCommand';
import { EditMutedMotionCommand } from 'state/commands/EditMutedMotionCommand';
import { SortLayersCommand } from 'state/commands/SortLayersCommand';
import { IosCloseButton } from '../components/iOSCloseButton';
import { PlatformType } from 'data/PlatformType';
import { generateTemplateUI } from 'state/FlyerState';

const SortableLayersList = SortableContainer(LayersList);

const TweenMax = (window as any).TweenMax;
const Power3 = (window as any).Power3;

interface Props {
  layers: Layer[];
  motionSource: MotionSource;
  soundtrack: Soundtrack | null;
  onClose: () => void;
}

interface State {
  layerToAddFilter: Layer | null;
  filterToPickValue: Effect | null;
  propertyToPickValue: string | null;
  propertyTypeToPickValue: string | null;
  audioSourceSelectRect: ClientRect | null;
  isSortingLayers: boolean;
  isEditingTimeline: boolean;
  isEditingMotionSource: boolean;
}

class Controller extends React.Component<Props> {
  soundtrackContainer: HTMLElement;
  layerToExpandDom: HTMLElement;
  state: State = {
    layerToAddFilter: null,
    filterToPickValue: null,
    propertyToPickValue: null,
    propertyTypeToPickValue: null,
    audioSourceSelectRect: null,
    isSortingLayers: false,
    isEditingTimeline: false,
    isEditingMotionSource: false
  };
  effectPickerTop: number;
  contractedScroll: number;
  expandedScroll: number;
  lastNumberOfLayers = 0;
  lastMotionSettings: string;

  stackEl = React.createRef<HTMLDivElement>();

  componentDidMount() {
    window.addEventListener('resize', this.updateCanvasOffset);
    this.updateCanvasOffset();
    if (isMobileLayout()) {
      const el = ReactDOM.findDOMNode(
        this as React.ReactInstance
      ) as HTMLElement;
      el.style.transform = `translateY(${window.screen.height}px)`;
    }
  }

  componentWillUnmount() {
    const initialWidth = parseInt(
      getCSSVarValue('--minimum-sidebar-width'),
      10
    );
    setCSSVar('--sidebar-width', `${initialWidth}px`);

    window.removeEventListener('resize', this.didResizeWindow);
    window.removeEventListener('resize', this.updateCanvasOffset);
  }

  didClickAddFilter = (layer: Layer, layerDom: HTMLElement) => {
    this.layerToExpandDom = layerDom;
    this.setState(
      {
        layerToAddFilter: layer
      },
      () => {
        storeDispatch(Actions.browseEffects());
      }
    );
  };
  cancelFilterAddition() {
    this.setState({
      layerToAddFilter: null
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // if (this.props.motionSource !== prevProps.motionSource) {
    //   this.updateSoundTrack();
    // }
    if (prevState.layerToAddFilter !== this.state.layerToAddFilter) {
      const el = ReactDOM.findDOMNode(
        this as React.ReactInstance
      ) as HTMLElement;

      if (!this.stackEl.current) {
        return;
      }
      // console.log('INITIAL SCROLL: ' + initialScroll);
      const stack = this.stackEl.current;
      const initialScroll = stack.scrollTop;
      const duration = 0.75;
      const ease = Power3.easeOut;
      const topBarHeight = stack.getBoundingClientRect().top;
      if (this.state.layerToAddFilter) {
        const confirmationEl = el.querySelector(
          '.confirmation-bar'
        ) as HTMLElement;
        if (!confirmationEl) {
          return;
        }
        const confirmationHeight =
          confirmationEl.getBoundingClientRect().height;
        confirmationEl.style.position = 'fixed';
        confirmationEl.style.width = el.getBoundingClientRect().width + 'px';
        this.effectPickerTop =
          this.layerToExpandDom.getBoundingClientRect().height;
        this.contractedScroll = initialScroll;
        const sidebarHeight = el.getBoundingClientRect().height;
        const tabBarEl = el.querySelector('.tab-bar-view');
        const tabBarHeight = tabBarEl
          ? tabBarEl.getBoundingClientRect().height
          : 0;
        const expansionHeight =
          sidebarHeight -
          this.layerToExpandDom.getBoundingClientRect().height -
          confirmationHeight -
          tabBarHeight;
        const picker = this.layerToExpandDom.querySelector(
          '.effect-picker'
        ) as HTMLElement;
        picker.style.height = expansionHeight + 'px';
        stack.style.overflowY = 'visible';
        const newLayerPosition =
          this.layerToExpandDom.getBoundingClientRect().top - topBarHeight;
        const endTransformation = newLayerPosition; // - topBarHeight;
        TweenMax.to(stack, duration, {
          y: -endTransformation,
          ease,
          clearProps: 'transform',
          onComplete: () => {
            confirmationEl.removeAttribute('style');
            stack.style.overflowY = 'hidden';
            stack.scrollTop = endTransformation;
            this.expandedScroll = stack.scrollTop;
          }
        });
        window.addEventListener('resize', this.didResizeWindow);
      } else {
        stack.style.overflowY = 'scroll';
        stack.scrollTop = this.contractedScroll;

        if (stack.scrollTop === 0) {
          TweenMax.from(stack, duration, {
            y: -(
              this.layerToExpandDom.getBoundingClientRect().top - topBarHeight
            ),
            ease,
            clearProps: 'transform',
            onComplete: () => {
              stack.removeAttribute('style');
            }
          });
        } else {
          // TODO: this doesn't work
        }
        window.removeEventListener('resize', this.didResizeWindow);
      }
    }
    this.updateCanvasOffset();
    if (this.lastNumberOfLayers > 0 && this.props.layers.length === 0) {
      this.setState({
        layerToAddFilter: null,
        elementToPickValue: null,
        filterToPickValue: null,
        propertyToPickValue: null,
        propertyTypeToPickValue: null,
        audioSourceSelectRect: null,
        isSortingLayers: false,
        isEditingTimeline: false,
        isEditingMotionSource: false
      });
    }
    this.lastNumberOfLayers = this.props.layers.length;
  }

  didResizeWindow = () => {
    const el = ReactDOM.findDOMNode(this as React.ReactInstance) as HTMLElement;

    const sidebarHeight = el.getBoundingClientRect().height;
    const expansionHeight = sidebarHeight - this.effectPickerTop;

    const picker = this.layerToExpandDom.querySelector(
      '.effect-picker'
    ) as HTMLElement;
    picker.style.height = expansionHeight + 'px';
    // console.log('Resize: ' + expansionHeight);
  };

  updateCanvasOffset = () => {
    const node = ReactDOM.findDOMNode(this) as HTMLElement;
    if (!node) {
      console.error('Wave node not available');
      return;
    }
    const controllerWidth = node.offsetWidth;
    setCSSVar('--sidebar-width', `${controllerWidth}px`);
    const canvas = document.querySelector('.canvas');
    if (!canvas) {
      console.error('Canvas node not available');
      return;
    }
    const canvasWidth = (canvas as HTMLElement).offsetWidth;
    const windowWidth = window.innerWidth;
    const currentOffset = (windowWidth - canvasWidth) / 2;
    let targetOffset =
      controllerWidth + (windowWidth - controllerWidth - canvasWidth) / 2;
    if (targetOffset + canvasWidth > windowWidth) {
      targetOffset = windowWidth - canvasWidth;
    }
    let offset = targetOffset - currentOffset;
    if (canvasWidth > windowWidth) {
      offset = 0;
    }
    // console.log(`Canvas Width: ${canvasWidth}`);
    // console.log(`controller Width: ${controllerWidth}`);
    // console.log(`OFFSET: ${offset}`);
    setCSSVar('--canvas-offset', `${offset}px`);
  };

  getPlaybackPosition = () => {
    const motionSource = this.props.motionSource;
    if (motionSource) {
      return motionSource.getNormalisedPosition();
    }
    return 0;
  };

  didSelectEffectToAdd = (effectId: string, cachedData: any) => {
    storeDispatch(
      Actions.showEffectPreview(
        this.state.layerToAddFilter!,
        effectId,
        cachedData
      )
    );
  };

  didCloseAudioSelector = () => {
    this.setState({ layerToChooseAudioSource: null });
  };

  onSortLayersStart = () => {
    this.setState({ isSortingLayers: true });
  };
  onSortLayers = (e: SortEvent) => {
    this.setState({ isSortingLayers: false });
    if (e.oldIndex === e.newIndex) {
      return;
    }
    // array is shown inverted
    const last: number = this.props.layers.length - 1;
    storeDispatch(new SortLayersCommand(last - e.oldIndex, last - e.newIndex));
  };

  confirmBarDidCancel = () => {
    const addingEffect = this.state.layerToAddFilter !== null;
    if (addingEffect) {
      const layer = this.state.layerToAddFilter!;
      this.setState({ layerToAddFilter: null }, () => {
        storeDispatch(Actions.cancelEffectPreview(layer));
      });
    } else if (this.state.isEditingMotionSource) {
      storeDispatch(Actions.cancelEditMotionSource());
      this.setState({ isEditingMotionSource: false });
    } else {
      storeDispatch(Actions.cancelEditTimeline());
      this.setState({ isEditingTimeline: false });
    }
  };

  confirmBarDidConfirm = () => {
    const addingEffect = this.state.layerToAddFilter !== null;
    if (addingEffect) {
      const layer = this.state.layerToAddFilter;
      if (layer) {
        const effect =
          layer.previewedCompositionEffect ??
          layer.effects[layer.effects.length - 1];
        const effectNameId = Object.getPrototypeOf(effect).constructor.id;
        this.setState({ layerToAddFilter: null }, () => {
          const effectSettings = effect.toObject();
          if ('signalSource' in effect) {
            (effectSettings as EffectData).signalSource = effect.signalSource;
          }
          storeDispatch(
            new AddEffectCommand(
              layer.id,
              effectNameId,
              effect.id,
              effectSettings
            )
          );
        });
      }
    } else if (this.state.isEditingMotionSource) {
      storeDispatch(new EditMutedMotionCommand());
      this.setState({ isEditingMotionSource: false });
    } else {
      storeDispatch(new EditTimelineCommand());
      this.setState({ isEditingTimeline: false });
    }
  };

  didStartEditingTimeline = (imgSrc: string) => {
    storeDispatch(Actions.editTimeline(imgSrc));
    this.setState({ isEditingTimeline: true });
  };

  didStartEditingMotionSource = () => {
    storeDispatch(Actions.editMotionSource());
    this.setState({ isEditingMotionSource: true });
  };

  shouldCancelLayersSorting = (
    e: React.MouseEvent<any, MouseEvent>
  ): boolean => {
    if (hasParentWithClassName(e.target as HTMLElement, 'effects')) {
      return true;
    }
    if (hasParentWithClassName(e.target as HTMLElement, 'separator')) {
      return true;
    }
    if (document.querySelector('ul.menu')) {
      return true;
    }
    if (document.querySelector('.custom-property')) {
      return true;
    }
    if (isPickingColor()) {
      return true;
    }
    return false;
  };

  render() {
    let maxFrames = 0;
    if (this.props.layers.length) {
      this.props.layers.forEach(layer => {
        maxFrames = Math.max(maxFrames, layer.frames.length);
      });
    }
    maxFrames = Math.min(MAX_FRAMES_AMOUNT, maxFrames + 1);
    const initialWidth = parseInt(
      getCSSVarValue('--minimum-sidebar-width'),
      10
    );
    const {
      isBeatflyerLite,
      templateEditMode,
      templateUI,
      templateOptions,
      detachColorTheme,
      editedShape,
      platform
    } = getStoreState().app;
    const { templateUIIntro, previewedEffect } = getStoreState().flyer;
    const rowHPadding = 10;
    const rightButtonWidth = 30;
    const rightButtonMargin = 6;
    const hrWidth = 2;
    const rightButtonsWidth =
      rightButtonWidth * 3 + hrWidth + rightButtonMargin * 3;
    const width = isBeatflyerLite
      ? initialWidth
      : Math.max(
          initialWidth,
          rowHPadding * 2 + rightButtonsWidth + 66 * maxFrames
        );
    const height = isBeatflyerLite ? 60 : 80;
    const addingEffect = this.state.layerToAddFilter !== null;
    const isEditingTimeline = this.state.isEditingTimeline;
    const isEditingMotionSource = this.state.isEditingMotionSource;
    const hasConfirmation =
      isEditingTimeline || isEditingMotionSource || addingEffect;
    const hasCanvasConfirmation = editedShape !== null;

    const orderedLayers = this.props.layers
      .slice(0)
      .filter(l => !isEditingTimeline || l.visible)
      .reverse();
    // let timelineView = null;
    // let hasAudio = false;
    // if (this.props.motionSource && !isEditingTimeline) {
    //   if (this.props.motionSource.hasAudio) {
    //     const soundtrack: Soundtrack = this.props.motionSource as Soundtrack;
    //     timelineView = (
    //       <AudioTimelineView
    //         ref={el => (this.waveView = el)}
    //         soundtrack={soundtrack}
    //         width={width}
    //         height={height}
    //         onEditTimeline={this.didStartEditingTimeline}
    //       />
    //     );
    //     hasAudio = true;
    //   } else {
    //     // add easing view
    //     const curve: EasingCurve = this.props.motionSource as EasingCurve;
    //     timelineView = (
    //       <EasingCurveView
    //         ref={el => (this.waveView = el)}
    //         curve={curve}
    //         width={width}
    //         height={height}
    //       />
    //     );
    //   }
    // }

    return (
      <div
        className={
          generateClassName('controller', {
            addingEffect,
            hasAudio: this.props.soundtrack !== null,
            hasConfirmation,
            sorting: this.state.isSortingLayers,
            hasCanvasConfirmation
          }) + ` controller--${maxFrames}frames`
        }
      >
        {templateEditMode !== TemplateEditMode.None &&
          !isMobileLayout() &&
          platform !== PlatformType.iOSApp && (
            <div style={{ padding: '0 16px' }}>
              <TabBarView
                items={['Basic', 'Advanced']}
                selectedIndex={
                  templateEditMode === TemplateEditMode.Simple ? 0 : 1
                }
                onChange={index =>
                  storeDispatch(
                    Actions.changeTemplateEditMode(
                      index === 0
                        ? TemplateEditMode.Simple
                        : TemplateEditMode.Advanced
                    )
                  )
                }
              />
            </div>
          )}
        {templateEditMode === TemplateEditMode.Simple ||
        (templateEditMode === TemplateEditMode.None && isMobileLayout()) ? (
          <>
            <TemplateUIView
              properties={
                templateUI ?? generateTemplateUI(getStoreState().flyer) ?? []
              }
              options={templateOptions}
              introType={templateUIIntro}
              detachColorTheme={detachColorTheme}
            />
            {isMobileLayout() && (
              <IosCloseButton onClick={this.props.onClose} />
            )}
          </>
        ) : (
          <>
            <div
              className="stack"
              ref={this.stackEl}
              onScroll={e => {
                if (e.target === this.stackEl.current) {
                  const event = new Event('stackScrolled');
                  window.dispatchEvent(event);
                }
              }}
            >
              {!(isBeatflyerLite === true && platform !== PlatformType.Figma) &&
                !isEditingTimeline &&
                !isEditingMotionSource && (
                  <div className="stack-header">
                    <NewFrameTypeMenu />
                  </div>
                )}
              <SortableLayersList
                items={orderedLayers}
                onSortStart={this.onSortLayersStart}
                onSortEnd={this.onSortLayers}
                lockAxis="y"
                // pressDelay={200}
                distance={10}
                layerToAddFilter={this.state.layerToAddFilter}
                onAddFilter={this.didClickAddFilter}
                getPlaybackPosition={this.getPlaybackPosition}
                onAddEffectSelect={this.didSelectEffectToAdd}
                useDragHandle={true}
                disabled={isEditingTimeline}
                isEditingTimeline={this.state.isEditingTimeline}
                shouldCancelStart={this.shouldCancelLayersSorting}
              />
            </div>
            {isEditingMotionSource && (
              <MotionSourceEditView
                width={width}
                height={height}
                source={this.props.motionSource as MuteMotionSource}
              />
            )}
            {hasConfirmation ? (
              <ConfirmationBar
                confirmLabel={addingEffect ? 'Add' : undefined}
                canConfirm={addingEffect ? previewedEffect !== null : true}
                onCancel={this.confirmBarDidCancel}
                onConfirm={this.confirmBarDidConfirm}
              />
            ) : (
              <MotionSourceView
                width={width}
                height={height}
                source={this.props.motionSource}
                canEditTimeline={this.props.layers.length > 0}
                onEditTimeline={this.didStartEditingTimeline}
                onEditMotionSource={this.didStartEditingMotionSource}
                soundtrack={this.props.soundtrack}
              />
            )}
          </>
        )}
      </div>
    );
  }
}

export { Controller };



// WEBPACK FOOTER //
// ./src/controller/Controller.tsx